import { signOut } from "@kovernow/flows/auth";
import { announce } from "@kovernow/flows/announce";
import { Button } from "@kovernow/react-ui/components/button";
import { homeQueryKeys } from "@kovernow/flows/home";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Page } from "../components/Page";
import { useServices } from "../core/app-context";
import { useMutationGate } from "../core/mutation-gate";
import { cleanUpAfterSession } from "../core/session-cleanup";
import { useAccountUser, useProfile } from "../core/queries";
import { useT } from "../core/use-t";

/** Hub rows: Profile gets a live summary, the rest a static subtitle. */
const LINKS = [
  { to: "/account/profile", labelKey: "nav.profile", summaryKey: undefined },
  { to: "/account/payment-methods", labelKey: "nav.paymentMethods", summaryKey: "account.paymentMethodsSummary" },
  { to: "/account/invoices", labelKey: "nav.invoices", summaryKey: "account.invoicesSummary" },
  { to: "/account/support", labelKey: "nav.support", summaryKey: "account.supportSummary" },
  { to: "/account/settings", labelKey: "nav.settings", summaryKey: "account.settingsSummary" },
] as const;

export function SignOutButton() {
  const services = useServices();
  const t = useT();
  const navigate = useNavigate();
  const gate = useMutationGate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        variant="outline"
        size="touch"
        data-testid="sign-out"
        aria-disabled={busy || gate.blocked ? true : undefined}
        aria-describedby={gate.blocked ? "sign-out-reason" : undefined}
        onClick={async () => {
          if (busy || gate.blocked) return;
          setBusy(true);
          try {
            await signOut({ api: services.authApi, identity: services.identity, session: services.session });
          } catch {
            // Firebase sign-out failed: the local session must still end.
            services.session.clear();
          } finally {
            // Nothing of this account may outlive its session.
            queryClient.clear();
            cleanUpAfterSession(services);
            setBusy(false);
            announce(t("auth.signedOut"));
            navigate("/sign-in", { replace: true });
          }
        }}
      >
        {busy ? t("common.loading") : t("nav.signOut")}
      </Button>
      {gate.blocked ? (
        <p id="sign-out-reason" className="text-sm text-muted-foreground">
          {gate.reason}
        </p>
      ) : null}
    </div>
  );
}

/** Profile row summary: name and verified state (fresh profile read). */
function useProfileSummary(): string | undefined {
  const t = useT();
  const profile = useProfile();
  const { account } = useAccountUser();
  if (!profile.data) return undefined;
  const name = profile.data.name ?? account?.name;
  const verified = profile.data.isAuthenticated === true;
  if (name) return t(verified ? "account.profileVerified" : "account.profileUnverified", { name });
  return t(verified ? "account.profileVerifiedNoName" : "account.profileUnverifiedNoName");
}

/** Account hub: Profile, Payment methods, Invoices, Support, Settings + sign-out. */
export function AccountHub() {
  const t = useT();
  const profileSummary = useProfileSummary();
  return (
    <Page titleKey="nav.account">
      <p className="text-muted-foreground">{t("account.intro")}</p>
      <ul className="mt-6 flex flex-col divide-y rounded-lg border">
        {LINKS.map((l) => {
          const summary = l.summaryKey ? t(l.summaryKey) : profileSummary;
          return (
            <li key={l.to}>
              <Link
                to={l.to}
                className="flex min-h-14 items-center justify-between gap-3 px-4 py-2 hover:bg-muted"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="font-semibold">{t(l.labelKey)}</span>
                  {summary ? (
                    <span className="text-sm text-muted-foreground" data-testid={`summary-${l.to.split("/").pop()}`}>
                      {summary}
                    </span>
                  ) : null}
                </span>
                <ChevronRight aria-hidden="true" className="size-5 shrink-0" />
              </Link>
            </li>
          );
        })}
      </ul>
      <div className="mt-8">
        <p className="mb-2 text-sm text-muted-foreground">{t("account.signOutBody")}</p>
        <SignOutButton />
      </div>
    </Page>
  );
}

/**
 * Settings (W-2): "Help and legal" (FAQ, Privacy, Terms from tenant config;
 * Contact us; My support cases behind `support_case_intake`), then Sign out.
 * No toggles (no biometrics, no email/marketing settings on web).
 */
export function SettingsPage() {
  const t = useT();
  const { config, homeApi } = useServices();
  const support = useQuery({
    queryKey: homeQueryKeys.supportFlags(),
    queryFn: () => homeApi.supportCaseIntake(),
    retry: false,
  });
  const legal = config.brand.legal;
  const external = [
    legal.faqUrl ? { id: "faq", href: legal.faqUrl, labelKey: "settings.faq" } : null,
    { id: "privacy", href: legal.privacyUrl, labelKey: "legal.privacy" },
    { id: "terms", href: legal.termsUrl, labelKey: "legal.terms" },
  ].filter((l): l is { id: string; href: string; labelKey: string } => l !== null);
  const internal = [
    { id: "contact", to: "/account/support", labelKey: "settings.contactUs" },
    ...(support.data === true ? [{ id: "cases", to: "/account/support", labelKey: "settings.supportCases" }] : []),
  ];
  const row = "flex min-h-12 items-center justify-between gap-3 px-4 font-semibold hover:bg-muted";
  return (
    <Page titleKey="nav.settings">
      <section aria-labelledby="settings-help">
        <h2 id="settings-help" className="text-lg font-semibold">
          {t("settings.helpLegal")}
        </h2>
        <ul className="mt-3 flex flex-col divide-y rounded-lg border" data-testid="help-legal">
          {external.map((l) => (
            <li key={l.id}>
              <a href={l.href} target="_blank" rel="noopener noreferrer" className={row}>
                <span>
                  {t(l.labelKey)} <span className="sr-only">{t("settings.opensInNewTab")}</span>
                </span>
                <ExternalLink aria-hidden="true" className="size-5 shrink-0" />
              </a>
            </li>
          ))}
          {internal.map((l) => (
            <li key={l.id}>
              <Link to={l.to} className={row}>
                {t(l.labelKey)}
                <ChevronRight aria-hidden="true" className="size-5 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="settings-sign-out" className="mt-8">
        <h2 id="settings-sign-out" className="mb-2 text-lg font-semibold">
          {t("settings.signOutTitle")}
        </h2>
        <p className="mb-2 text-sm text-muted-foreground">{t("account.signOutBody")}</p>
        <SignOutButton />
      </section>
    </Page>
  );
}
